import clsx from 'clsx';
import { memo, useMemo } from 'react';

import type { LandingHeroRotatorPair } from '@/interfaces/LandingHeroRotatorPair';

import styles from '../LandingPage/LandingPage.module.scss';
import localStyles from './LandingHeroRotator.module.scss';

interface LandingHeroRotatorProps {
    connector: string;
    pairs: LandingHeroRotatorPair[];
}

const LandingHeroRotatorComponent = ({ connector, pairs }: LandingHeroRotatorProps) => {
    const subjectTermNodes = useMemo(
        () =>
            pairs.map(({ key, subject }, index) => (
                <span
                    className={styles.heroRotatingTerm}
                    data-testid={`components-landing-landing-page-hero-rotator-subject-copy-${index + 1}`}
                    key={`subject:${key}`}
                >
                    <span className={styles.heroMaskedText}>{subject}</span>
                </span>
            )),
        [pairs]
    );
    const audienceTermNodes = useMemo(
        () =>
            pairs.map(({ audience, key }, index) => (
                <span
                    className={styles.heroRotatingTerm}
                    data-testid={`components-landing-landing-page-hero-rotator-audience-copy-${index + 1}`}
                    key={`audience:${key}`}
                >
                    <span className={styles.heroMaskedText}>{audience}</span>
                </span>
            )),
        [pairs]
    );
    const phraseTermNodes = useMemo(
        () =>
            pairs.map(({ audience, key, subject }, index) => (
                <span
                    className={styles.heroRotatingPhrase}
                    data-testid={`components-landing-landing-page-hero-rotator-phrase-copy-${index + 1}`}
                    key={`phrase:${key}`}
                >
                    <span className={styles.heroMaskedText}>{`${subject} ${connector} ${audience}`}</span>
                </span>
            )),
        [connector, pairs]
    );

    return (
        <span aria-hidden="true" className={clsx(styles.heroRotator, localStyles.root)} data-testid="components-landing-landing-page-hero-rotator">
            <span className={styles.heroRotatorPhraseSlot} data-testid="components-landing-landing-page-hero-rotator-phrase-slot">
                {phraseTermNodes}
            </span>
            <span className={styles.heroRotatorSplit} data-testid="components-landing-landing-page-hero-rotator-split">
                <span
                    className={clsx(styles.heroRotatorSlot, styles.heroRotatorSlotSubject)}
                    data-testid="components-landing-landing-page-hero-rotator-subject-slot"
                >
                    {subjectTermNodes}
                </span>
                <span className={styles.heroRotatorConnector} data-testid="components-landing-landing-page-hero-rotator-connector">
                    {connector}
                </span>
                <span
                    className={clsx(styles.heroRotatorSlot, styles.heroRotatorSlotAudience)}
                    data-testid="components-landing-landing-page-hero-rotator-audience-slot"
                >
                    {audienceTermNodes}
                </span>
            </span>
        </span>
    );
};

LandingHeroRotatorComponent.displayName = 'LandingHeroRotator';

export const LandingHeroRotator = memo(LandingHeroRotatorComponent);
